import { PushTokenService, addNativePushTokenRefreshListener } from "push-notifications";
import type { IPushLogger } from "push-notifications";
import { isNativeApp } from "../../../../utils/native/nativeHelpers";
import { FeatureFlagName } from "../../../../utils/featureFlagName";
import { ServerFlags } from "../../../../store/data/types";
import type { IFeatureFlagsService } from "../../../../services/featureFlags";
import type { DriveStore } from "../../../../store/reducers";
import type AppView from "../../../../app.view";
import { DriveApiPushTokenAdapter } from "./driveApiPushTokenAdapter";
import * as logger from "../../../../utils/logger";

let removeRefreshListener: (() => void) | undefined;

interface IDrivePushTokenContext {
    featureFlagService: IFeatureFlagsService;
    store: DriveStore;
}

function isFedRampEnvironment(store: DriveStore): boolean {
    return store.getState().data.serverFlags === ServerFlags.None;
}

function isRunningInMobileAppWithPushSupport(): boolean {
    return isNativeApp() && PushTokenService.hasNativeBridge();
}

function isPushTokenFeatureFlagEnabled(featureFlagService: IFeatureFlagsService): boolean {
    return featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_DRIVE_PUSH_TOKEN_REGISTRATION);
}

export function shouldUsePushTokenFunctionalityDrive(context: IDrivePushTokenContext): boolean {
    if (!isRunningInMobileAppWithPushSupport()) {
        return false;
    }
    if (!isPushTokenFeatureFlagEnabled(context.featureFlagService)) {
        return false;
    }
    if (isFedRampEnvironment(context.store)) {
        return false;
    }
    return true;
}

function createPushLogger(userId?: string): IPushLogger {
    return {
        log(message: string): void {
            logger.log(logger.Source.USER, `[PushToken] ${ message }`, userId);
        },
        error(message: string, error?: any): void {
            logger.error(logger.Source.USER, `[PushToken] ${ message }`, userId, error);
        }
    };
}

export async function reregisterPushTokenAfterRefresh(app: AppView, store: DriveStore): Promise<void> {
    let userId: string | undefined;
    try {
        if (!shouldUsePushTokenFunctionalityDrive({ featureFlagService: app.featureFlagService, store })) {
            return;
        }

        userId = app.userService.getCurrentUser()?.id;
        if (!userId) {
            return;
        }

        const pushTokenService = PushTokenService.getInstance();
        if (!pushTokenService.isInitialized()) {
            return;
        }
        await pushTokenService.refreshPushTokenRegistration(userId);
    } catch (error) {
        logger.error(logger.Source.USER, "[PushToken] Failed to re-register push token after refresh", userId, error);
    }
}

function installPushTokenRefreshListener(app: AppView, store: DriveStore): void {
    if (removeRefreshListener) {
        return;
    }
    removeRefreshListener = addNativePushTokenRefreshListener(() => {
        void reregisterPushTokenAfterRefresh(app, store);
    });
}

export async function registerPushTokenAfterLogin(app: AppView, store: DriveStore): Promise<void> {
    if (!shouldUsePushTokenFunctionalityDrive({ featureFlagService: app.featureFlagService, store })) {
        return;
    }

    const currentUser = app.userService.getCurrentUser();
    if (!currentUser?.id) {
        return;
    }

    try {
        installPushTokenRefreshListener(app, store);
        const pushTokenService = PushTokenService.getInstance();
        const apiAdapter = new DriveApiPushTokenAdapter(app.api);
        const pushLogger = createPushLogger(currentUser.id);

        await pushTokenService.initialize(apiAdapter, pushLogger);
        await pushTokenService.registerPushTokenWithBackend(currentUser.id);
    } catch (error) {
        // Push token registration must NEVER block login.
        // The driver's ability to use the Drive app is more important than push notifications.
        logger.error(logger.Source.USER, "[PushToken] Failed to register push token after login", currentUser.id, error);
    }
}

export async function unregisterPushTokenOnDriveLogout(app: AppView, store: DriveStore): Promise<void> {
    removeRefreshListener?.();
    removeRefreshListener = undefined;
    if (!shouldUsePushTokenFunctionalityDrive({ featureFlagService: app.featureFlagService, store })) {
        return;
    }

    const currentUser = app.userService.getCurrentUser();
    const userId = currentUser?.id;

    let pushTokenService;
    try {
        pushTokenService = PushTokenService.getInstance();
    } catch (error) {
        // Native bridge unavailable — nothing to unregister.
        logger.error(logger.Source.USER, "[PushToken] Failed to get push token service on logout — skipping unregister", userId, error);
        return;
    }

    if (pushTokenService.isInitializationPending()) {
        // Login never completed push token setup — nothing to unregister.
        // The token will become stale and be cleaned up by the backend cron job.
        logger.log(logger.Source.USER, "[PushToken] Skipped unregister on logout — initialization was still pending", userId);
        return;
    }

    try {
        const apiAdapter = new DriveApiPushTokenAdapter(app.api);
        const pushLogger = createPushLogger(userId);
        await pushTokenService.initialize(apiAdapter, pushLogger);
    } catch (error) {
        // Cannot reach native bridge during logout — bail out silently.
        logger.error(logger.Source.USER, "[PushToken] Failed to initialize during logout — skipping unregister", userId, error);
        return;
    }

    try {
        await pushTokenService.unregisterPushToken(userId);
        logger.log(logger.Source.USER, "[PushToken] Successfully unregistered push token on logout", userId);
    } catch (error) {
        // Push token removal must NEVER block logout.
        // Stale tokens are harmless — cleaned up by the backend cron job.
        logger.error(logger.Source.USER, "[PushToken] Failed to unregister push token on logout", userId, error);
    }
}
